import {
  isSerializable,
  isValidActivityId,
  isValidInteractiveData,
  isValidMultipleChoiceData,
  isValidRequestSpeechEvaluationData,
  isValidSaveData,
  isValidShortAnswerData,
  isValidSpeakingData,
  isValidSubmitData,
} from '../utils/isValid';
import { ChildBridge, Connection } from '../bridge/child-bridge/child-bridge';
import {
  Activity,
  JsonValue,
  Mode,
  SaveData,
  SpeechEvaluationRequestData,
  SubmitData,
  UploadFileRequest,
  UploadFileResponse,
  SpeechEvaluationResult,
} from '../bridge/types';
import Uppy from '@uppy/core';
import Korean from '@uppy/locales/lib/ko_KR';
import AwsS3 from '@uppy/aws-s3';

export class FrameActivitySdk {
  protected handlers: ChildBridge;
  private connection: Connection | null = null;
  private savedData: SaveData | null = null;
  private activities?: Activity[];

  constructor() {
    this.handlers = new ChildBridge();
  }

  private async ensureInitialized() {
    if (!this.connection) {
      this.connection = await this.handlers.connect();
    }
  }

  // 정답 등록
  public async registerActivities(data: Activity[]) {
    try {
      await this.ensureInitialized();

      // 데이터 배열 유효성 검사
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('유효하지 않은 데이터 형식입니다.');
      }

      // activityId 중복 검사
      if (!isValidActivityId(data)) {
        throw new Error('중복된 activityId가 존재합니다.');
      }

      // 각 활동 타입별 유효성 검사
      const isValidData = data.every((activity) => {
        switch (activity.type) {
          case 'MULTIPLE_CHOICE':
            return isValidMultipleChoiceData(activity);
          case 'SHORT_ANSWER':
            return isValidShortAnswerData(activity);
          case 'INTERACTIVE':
            return isValidInteractiveData(activity);
          case 'SPEAKING':
            return isValidSpeakingData(activity);
          default:
            return false;
        }
      });

      if (!isValidData) {
        throw new Error('유효하지 않은 활동 데이터가 포함되어 있습니다.');
      }

      // 활동 등록
      await this.handlers.registerActivities(data);
      this.activities = data;
    } catch (error) {
      throw new Error(`활동 등록 오류: ${error}`);
    }
  }

  // 답안 제출
  public async submit(data: SubmitData[]) {
    try {
      await this.ensureInitialized();

      if (!isValidSubmitData(data)) {
        throw new Error('유효하지 않은 답안 데이터입니다.');
      }

      await this.handlers.submit(data);
    } catch (error) {
      throw new Error(`답안 제출 오류: ${error}`);
    }
  }

  // 저장하기
  public async save(key: string, data: SaveData) {
    try {
      await this.ensureInitialized();

      if (!isValidSaveData(data)) {
        throw new Error('유효하지 않은 저장 데이터입니다.');
      }

      if (!isSerializable(data)) {
        throw new Error('직렬화할 수 없는 데이터가 포함되어 있습니다.');
      }

      this.savedData = data || {};
      await this.handlers.save(key, data);
    } catch (error) {
      throw new Error(`저장 오류: ${error}`);
    }
  }

  // 불러오기
  public async load(key: string): Promise<JsonValue | null> {
    try {
      await this.ensureInitialized();

      const savedData = await this.handlers.load(key);
      this.savedData = savedData;
      return savedData;
    } catch (error) {
      throw new Error(`불러오기 오류: ${error}`);
    }
  }

  // 현재 모드 가져오기
  public async getMode(): Promise<Mode> {
    try {
      await this.ensureInitialized();

      return await this.handlers.getMode();
    } catch (error) {
      throw new Error(`모드 조회 오류: ${error}`);
    }
  }

  // 파일 업로드
  public async uploadFileWithBlob(params: UploadFileRequest): Promise<UploadFileResponse> {
    try {
      await this.ensureInitialized();

      const { blob, fileName, activityId } = params;

      // 파일 정보 준비
      const fileType = blob.type;
      const fileSize = blob.size;

      const { uploadUrl, downloadUrl, fileId } = await this.handlers.getFileUploadUrl({
        fileName,
        fileType,
        fileSize,
        activityId: activityId || null,
      });

      const uppy = new Uppy({
        autoProceed: true,
        locale: Korean,
      });

      uppy.use(AwsS3, {
        limit: 5,
        timeout: 3600000,
        getUploadParameters: (file) => {
          return {
            method: 'PUT',
            url: uploadUrl,
            headers: {
              'Content-Type': fileType,
              'Content-Disposition': `attachment; filename="${file.name}"`,
            },
            fields: {
              downloadUrl,
              fileId,
            },
          };
        },
      });

      return new Promise((resolve, reject) => {
        uppy.on('upload-success', (file, response) => {
          resolve({
            url: downloadUrl,
          });
        });

        uppy.on('upload-error', (file, error, response) => {
          reject(new Error(`파일 업로드 실패: ${error}`));
        });

        // 파일을 Uppy에 추가
        const file = {
          name: fileName,
          type: fileType,
          size: fileSize,
          data: blob,
        };

        uppy.addFile({
          name: file.name,
          type: file.type,
          size: file.size,
          data: file.data,
        });
      });
    } catch (error) {
      throw new Error(`파일 업로드 오류: ${error}`);
    }
  }

  // 발화평가 요청
  public async requestSpeechEvaluation(data: SpeechEvaluationRequestData): Promise<string> {
    try {
      await this.ensureInitialized();

      if (!isValidRequestSpeechEvaluationData(data)) {
        throw new Error('필수 파라미터가 누락되었습니다.');
      }

      const sttId = await this.handlers.requestSpeechEvaluation(data);
      return sttId;
    } catch (error) {
      throw new Error(`발화평가 요청 오류: ${error}`);
    }
  }

  // 발화평가 결과 조회
  public async getSpeechEvaluationResult(sttId: string): Promise<SpeechEvaluationResult> {
    try {
      await this.ensureInitialized();

      if (!sttId || typeof sttId !== 'string') {
        throw new Error('유효하지 않은 sttId입니다.');
      }

      const result = await this.handlers.getSpeechEvaluationResult(sttId);
      return result;
    } catch (error) {
      throw new Error(`발화평가 결과 조회 오류: ${error}`);
    }
  }

  // 높이 설정
  public async setHeight(height: number): Promise<boolean> {
    try {
      await this.ensureInitialized();

      if (typeof height !== 'number' || height < 0) {
        throw new Error('높이는 0 이상의 값이여야 합니다.');
      }

      const result = await this.handlers.setHeight(height);
      return result;
    } catch (error) {
      throw new Error(`높이 설정 오류: ${error}`);
    }
  }
}

export default FrameActivitySdk;
